import {
  memo,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  Box,
  Typography,
  Chip,
  Paper,
  Tooltip,
  Select,
  MenuItem,
  CircularProgress,
  Divider,
  Tab,
  Tabs,
} from '@mui/material'
import ScheduleIcon from '@mui/icons-material/Schedule'
import KitchenIcon from '@mui/icons-material/Countertops'
import LineIcon from '@mui/icons-material/PrecisionManufacturing'
import { useQueryClient } from 'react-query'
import { InputRow } from 'Features/SchedulerView/SchedulerView.types'
import {
  fetchSchedulerData,
  ScheduleVariantContext,
  useSchedulerData,
} from 'Features/SchedulerView/SchedulerView.data'
import {
  adjustOpacity,
  getKitchenColor,
} from 'Features/SchedulerView/RenderCell.view'
import {
  SchedulerTabsProps,
  WorkstationDownTimeRowType,
  SynchronizedLinesRowType,
  SchedulerResourceAndMaterialConstraintsRowType,
  ProductionRequirementRowType,
  GanttResourceType as ResourceType,
  GanttResourceData as ResourceData,
  GanttProductionBar as ProductionBar,
  GanttCleaningBar as CleaningBar,
  GanttDowntimeInterval as DowntimeInterval,
} from './Scheduler.types'
import {
  useScheduleProductionRequirements,
  expandNotProducedKeys,
  SchedulerComment,
} from './Scheduler.data'
import {
  CommentTarget,
  commentsFor,
  downtimeAnchor,
  useCommentIndex,
  useCurrentUserEmail,
} from './ScheduleComments.anchor'
import {
  CommentDot,
  COMMENT_ACCENT_DOWNTIME,
  CommentHoverBlock,
  ScheduleCommentPopover,
} from './ScheduleCommentPopover.view'
import { ScheduleCrewUsageView } from './ScheduleCrewUsage.view'
import { ScheduleResourceUsageView } from './ScheduleResourceUsage.view'
import { ScheduleDemandFulfilmentView } from './ScheduleDemandFulfilment.view'
import { ScheduleTemperatureView } from './ScheduleTemperatureView.view'
import DownloadIcon from '@mui/icons-material/Download'
import ImageIcon from '@mui/icons-material/Image'
import { IconButton } from '@mui/material'

// ─── Constants ────────────────────────────────────────────────────────────────

const HOURS = 168
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const LEFT_COL_WIDTH = 150
const ROW_HEIGHT = 36
const TIME_AXIS_HEIGHT = 50
const DAY_BOUNDARIES = [0, 24, 48, 72, 96, 120, 144]

/**
 * Sub-tabs of the schedule view hidden for now.
 *
 * Temporary and reversible: empty the set to bring the tab back. The view,
 * its data and its props are untouched — this only removes the tab and the
 * branch that renders it, in step, so the tab strip can never offer something
 * that renders nothing and the state can never render something with no tab.
 *
 * `temperature` is the "Sync Groups" tab (the view kept its original name after
 * the label was changed).
 */
const HIDDEN_SCHEDULE_SUBTABS: ReadonlySet<string> = new Set<string>([
  'temperature',
  // Crew & Capacity — see HIDDEN_TABS in FactoryScheduleView.view.tsx.
  'crew',
])

const isSubTabHidden = (id: string): boolean => HIDDEN_SCHEDULE_SUBTABS.has(id)

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Parse "(0,8),(24,32)" into [[0,8],[24,32]] */
const parseDowntimeStr = (s: string): [number, number][] => {
  if (!s || s === 'null' || s === 'None') return []
  const intervals: [number, number][] = []
  const re = /\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*\)/g
  let m: RegExpExecArray | null
  while ((m = re.exec(s)) !== null) {
    intervals.push([Math.round(parseFloat(m[1])), Math.round(parseFloat(m[2]))])
  }
  return intervals
}

/** Format hour to readable time string */
const formatTime = (hour: number): string => {
  const day = DAY_LABELS[Math.floor(hour / 24)]
  const h = Math.floor(hour % 24)
  return `${day} ${h.toString().padStart(2, '0')}:00`
}

/** One entry of `additional_line_changeovers` — the wire format
 *  `changeover_events_to_json` writes in postprocess.rs. `cost` is present
 *  only on events that carry one (bottle-format changeovers). */
type LineChangeoverEvent = {
  start: number
  end: number
  reason?: string
  cost?: number
}

const parseLineChangeoverEvents = (
  raw: string | null | undefined
): LineChangeoverEvent[] => {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

/** Percentage position for a given hour within the 168h week */
const pct = (hour: number): string => `${(hour / HOURS) * 100}%`

// ─── Types ────────────────────────────────────────────────────────────────────
// ResourceData/ProductionBar/CleaningBar/DowntimeInterval live in
// Scheduler.types.ts (as Gantt*) so ScheduleResourceUsage.view.tsx can read
// them too without an import cycle between the two view files.

/** A base-material lane (its kitchen runs) plus the product lanes nested
 *  beneath it (their line runs) for the By Product view. */
type ProductScheduleLane = {
  baseMaterial: string
  description: string
  kitchen: ResourceData
  products: ResourceData[]
}

// ─── GanttRow (memoized) ─────────────────────────────────────────────────────

type GanttRowProps = {
  resource: ResourceData
  isEven: boolean
  onBarClick?: (bar: ProductionBar, resourceType: ResourceType) => void
  currentHour?: number
  notProducedIds?: Set<string>
  /** Render the left label as a bold section header (used by the By Product view). */
  isHeader?: boolean
  /** Indent the left label (nested product rows under a base material). */
  indent?: boolean
  /** Optional icon rendered to the left of the resource name. */
  icon?: ReactNode
  /** Every comment on this week, keyed by anchor. Passed whole rather than
   *  per-bar so a row with no comments does no work beyond one map lookup. */
  commentIndex?: Map<string, SchedulerComment[]>
  /** The week the bars belong to — half of every anchor key. */
  commentWeek?: number
  /** Opens the comment popover for a batch, at the click position. Absent
   *  where comments aren't available (no schedule id). */
  onOpenComments?: (
    target: CommentTarget,
    position: { top: number; left: number }
  ) => void
  commentsReadOnly?: boolean
}

const GanttRow = memo(
  ({
    resource,
    isEven,
    onBarClick,
    currentHour = 0,
    notProducedIds,
    isHeader = false,
    indent = false,
    icon,
    commentIndex,
    commentWeek = 1,
    onOpenComments,
    commentsReadOnly = false,
  }: GanttRowProps) => {
    const bgColor = isEven ? '#fafafa' : '#ffffff'

    return (
      <Box
        sx={{
          display: 'flex',
          height: ROW_HEIGHT,
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        {/* Resource label */}
        <Box
          sx={{
            width: LEFT_COL_WIDTH,
            minWidth: LEFT_COL_WIDTH,
            maxWidth: LEFT_COL_WIDTH,
            display: 'flex',
            alignItems: 'center',
            pl: indent ? 3 : 1,
            pr: 1,
            gap: 0.5,
            bgcolor: isHeader ? '#eeeeee' : bgColor,
            borderRight: '1px solid #e0e0e0',
            overflow: 'hidden',
          }}
        >
          {icon && (
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              {icon}
            </Box>
          )}
          <Box
            sx={{
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Typography
              noWrap
              sx={{
                fontSize: '0.7rem',
                fontWeight: isHeader ? 700 : 500,
                color: '#333',
                lineHeight: 1.15,
              }}
              title={resource.name}
            >
              {resource.name}
            </Typography>
            {resource.description && (
              <Typography
                noWrap
                sx={{
                  fontSize: '0.55rem',
                  color: 'text.secondary',
                  lineHeight: 1.1,
                }}
                title={resource.description}
              >
                {resource.description}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Gantt area */}
        <Box
          sx={{
            flex: 1,
            position: 'relative',
            bgcolor: isHeader ? '#eeeeee' : bgColor,
            overflow: 'hidden',
          }}
        >
          {/* Day separator lines */}
          {DAY_BOUNDARIES.map((hour) => (
            <Box
              key={`sep-${hour}`}
              sx={{
                position: 'absolute',
                left: pct(hour),
                top: 0,
                bottom: 0,
                width: '1px',
                bgcolor: 'rgba(0,0,0,0.12)',
                zIndex: 1,
              }}
            />
          ))}

          {/* 6-hour tick lines */}
          {Array.from({ length: 28 }, (_, i) => i * 6).map((hour) =>
            !DAY_BOUNDARIES.includes(hour) ? (
              <Box
                key={`tick-${hour}`}
                sx={{
                  position: 'absolute',
                  left: pct(hour),
                  top: 0,
                  bottom: 0,
                  width: '1px',
                  bgcolor: 'rgba(0,0,0,0.04)',
                  zIndex: 1,
                }}
              />
            ) : null
          )}

          {/* Downtime hatched areas */}
          {resource.downtimeIntervals.map((dt, idx) => {
            // Downtime comments are STORED per resource per week — a window
            // has no id to key on — but they are shown per window: this bar
            // gets the comments written on it, plus any resource-level note,
            // which applies to the whole week. Same thread the Workstation
            // Downtime editor shows, split the same way.
            const dtComments = commentsFor(
              commentIndex,
              downtimeAnchor(resource.name, resource.type),
              commentWeek,
              { startTime: dt.start, endTime: dt.end }
            )
            const canComment = Boolean(onOpenComments)
            // This bar's hours ARE the anchor window now, so a new comment
            // records which window it was written on and reads back onto it.
            const target = {
              ...downtimeAnchor(resource.name, resource.type),
              startTime: dt.start,
              endTime: dt.end,
              productDescription: `${resource.name} downtime`,
            }
            return (
              <Tooltip
                key={`dt-${idx}`}
                leaveDelay={canComment ? 160 : 0}
                title={
                  <Box sx={{ fontSize: '0.7rem' }}>
                    <div>
                      <strong>Downtime</strong>
                    </div>
                    <div>
                      {formatTime(dt.start)} - {formatTime(dt.end)} (
                      {dt.end - dt.start}h)
                    </div>
                    {canComment && (
                      <CommentHoverBlock
                        comments={dtComments}
                        readOnly={commentsReadOnly}
                        onAdd={(e) =>
                          onOpenComments?.(target, {
                            top: e.clientY,
                            left: e.clientX,
                          })
                        }
                      />
                    )}
                  </Box>
                }
                arrow
                placement="top"
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: pct(dt.start),
                    width: pct(dt.end - dt.start),
                    top: 0,
                    bottom: 0,
                    zIndex: 2,
                    background:
                      'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(211,47,47,0.25) 3px, rgba(211,47,47,0.25) 6px)',
                    borderLeft: '1px solid rgba(211,47,47,0.4)',
                    borderRight: '1px solid rgba(211,47,47,0.4)',
                  }}
                >
                  {dtComments.length > 0 && (
                    <CommentDot
                      allResolved={dtComments.every((c) => c.resolved)}
                      accent={COMMENT_ACCENT_DOWNTIME}
                    />
                  )}
                </Box>
              </Tooltip>
            )
          })}

          {/* Production bars */}
          {resource.productionBars.map((bar, idx) => {
            const barWidth = ((bar.end - bar.start) / HOURS) * 100
            const showLabel = bar.end - bar.start >= 3
            const barKey = `${bar.productCode}_${resource.name}_${bar.start}`
            const isNotProduced = notProducedIds?.has(barKey) ?? false
            const barComments = commentsFor(
              commentIndex,
              bar.anchor,
              commentWeek
            )
            const canComment = Boolean(bar.anchor && onOpenComments)
            return (
              <Tooltip
                key={`prod-${idx}`}
                // The tooltip is the hover pop-up the comment affordance lives
                // in, so it has to survive the pointer travelling into it —
                // MUI tooltips are interactive by default, the delay is what
                // makes the trip across the gap comfortable.
                leaveDelay={canComment ? 160 : 0}
                title={
                  <Box sx={{ fontSize: '0.7rem' }}>
                    <div>
                      <strong>
                        {bar.productDescription || bar.productCode}
                      </strong>
                      {isNotProduced && ' — Not Produced'}
                    </div>
                    {bar.productDescription && (
                      <div style={{ opacity: 0.6, fontSize: '0.65rem' }}>
                        {bar.productCode}
                      </div>
                    )}
                    <div>Base material: {bar.baseMaterial}</div>
                    <div>Quantity: {bar.quantity.toLocaleString()}</div>
                    <div>
                      {formatTime(bar.start)} - {formatTime(bar.end)}
                    </div>
                    <div>Duration: {(bar.end - bar.start).toFixed(1)}h</div>
                    <div>Run rate: {bar.runRate}</div>
                    {isNotProduced && (
                      <div
                        style={{
                          marginTop: 4,
                          color: '#ff9800',
                          fontWeight: 600,
                        }}
                      >
                        Click to undo
                      </div>
                    )}
                    {canComment && (
                      <CommentHoverBlock
                        comments={barComments}
                        readOnly={commentsReadOnly}
                        onAdd={(e) =>
                          onOpenComments?.(
                            {
                              ...bar.anchor!,
                              startTime: bar.start,
                              endTime: bar.end,
                              productDescription: bar.productDescription,
                            },
                            { top: e.clientY, left: e.clientX }
                          )
                        }
                      />
                    )}
                  </Box>
                }
                arrow
                placement="top"
              >
                <Box
                  onClick={() => onBarClick?.(bar, resource.type)}
                  sx={{
                    position: 'absolute',
                    left: `${(bar.start / HOURS) * 100}%`,
                    width: `${barWidth}%`,
                    top: 2,
                    height: ROW_HEIGHT - 4,
                    // 40% opacity — same softening RenderCell.view.tsx uses for
                    // the table view; the raw kitchen palette is full primary
                    // saturation and far too glaring rendered solid.
                    bgcolor: adjustOpacity(
                      getKitchenColor(bar.kitchen || ''),
                      0.4
                    ),
                    borderRadius: '2px',
                    zIndex: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    border: isNotProduced
                      ? '2px dashed #ed6c02'
                      : '1px solid rgba(0,0,0,0.15)',
                    opacity: isNotProduced ? 0.5 : 1,
                    ...(isNotProduced && {
                      background: `repeating-linear-gradient(45deg, ${adjustOpacity(getKitchenColor(bar.kitchen || ''), 0.4)}, ${adjustOpacity(getKitchenColor(bar.kitchen || ''), 0.4)} 4px, rgba(255,255,255,0.4) 4px, rgba(255,255,255,0.4) 8px)`,
                      bgcolor: 'transparent',
                    }),
                    '&:hover': {
                      filter: 'brightness(1.15)',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                    },
                  }}
                >
                  {showLabel && (
                    <Typography
                      noWrap
                      sx={{
                        fontSize: '0.6rem',
                        // Dark ink, not white — the fill above is now a pale
                        // 40%-opacity pastel, so white text would disappear.
                        color: '#1a1a1a',
                        fontWeight: 600,
                        px: 0.5,
                      }}
                    >
                      {bar.productCode}
                    </Typography>
                  )}
                  {barComments.length > 0 && (
                    <CommentDot
                      allResolved={barComments.every((c) => c.resolved)}
                    />
                  )}
                </Box>
              </Tooltip>
            )
          })}

          {/* Cleaning bars */}
          {resource.cleaningBars.map((bar, idx) => {
            const barWidth = ((bar.end - bar.start) / HOURS) * 100
            return (
              <Tooltip
                key={`clean-${idx}`}
                title={`${bar.type === 'preclean' ? 'Pre-clean' : 'CIP Clean'} (${bar.cip}): ${formatTime(bar.start)} - ${formatTime(bar.end)}`}
                arrow
                placement="top"
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${(bar.start / HOURS) * 100}%`,
                    width: `${barWidth}%`,
                    top: ROW_HEIGHT * 0.3,
                    height: ROW_HEIGHT * 0.6,
                    bgcolor: bar.type === 'preclean' ? '#ffa726' : '#ff9800',
                    borderRadius: '2px',
                    zIndex: 4,
                    opacity: 0.85,
                    border: '1px solid rgba(0,0,0,0.1)',
                    '&:hover': {
                      opacity: 1,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                    },
                  }}
                />
              </Tooltip>
            )
          })}
        </Box>
      </Box>
    )
  }
)

GanttRow.displayName = 'GanttRow'

// ─── SectionHeader ────────────────────────────────────────────────────────────

const SectionHeader = ({ label }: { label: string }) => (
  <Box
    sx={{
      display: 'flex',
      height: 28,
      bgcolor: '#eeeeee',
      borderBottom: '1px solid #d0d0d0',
    }}
  >
    <Box
      sx={{
        width: LEFT_COL_WIDTH,
        minWidth: LEFT_COL_WIDTH,
        display: 'flex',
        alignItems: 'center',
        px: 1,
      }}
    >
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#555' }}>
        {label}
      </Typography>
    </Box>
    <Box sx={{ flex: 1 }} />
  </Box>
)

// ─── TimeAxis ─────────────────────────────────────────────────────────────────

const TimeAxis = () => (
  <Box
    sx={{
      display: 'flex',
      height: TIME_AXIS_HEIGHT,
      borderBottom: '2px solid #bdbdbd',
    }}
  >
    {/* Empty left column */}
    <Box
      sx={{
        width: LEFT_COL_WIDTH,
        minWidth: LEFT_COL_WIDTH,
        borderRight: '1px solid #e0e0e0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography sx={{ fontSize: '0.65rem', color: '#999', fontWeight: 600 }}>
        Resource
      </Typography>
    </Box>

    {/* Time labels */}
    <Box sx={{ flex: 1, position: 'relative' }}>
      {/* Day labels */}
      {DAY_LABELS.map((day, dayIdx) => (
        <Box
          key={`day-${day}`}
          sx={{
            position: 'absolute',
            left: pct(dayIdx * 24),
            width: pct(24),
            top: 0,
            height: TIME_AXIS_HEIGHT / 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRight: '1px solid #e0e0e0',
            borderBottom: '1px solid #eeeeee',
          }}
        >
          <Typography
            sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#444' }}
          >
            {day}
          </Typography>
        </Box>
      ))}

      {/* Hour tick labels (every 6 hours) */}
      {Array.from({ length: 28 }, (_, i) => i * 6).map((hour) => (
        <Box
          key={`hour-${hour}`}
          sx={{
            position: 'absolute',
            left: pct(hour),
            top: TIME_AXIS_HEIGHT / 2,
            height: TIME_AXIS_HEIGHT / 2,
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography sx={{ fontSize: '0.55rem', color: '#888' }}>
            {hour % 24}
          </Typography>
        </Box>
      ))}

      {/* Day separator lines */}
      {DAY_BOUNDARIES.map((hour) => (
        <Box
          key={`axis-sep-${hour}`}
          sx={{
            position: 'absolute',
            left: pct(hour),
            top: 0,
            bottom: 0,
            width: '1px',
            bgcolor: 'rgba(0,0,0,0.2)',
          }}
        />
      ))}
    </Box>
  </Box>
)

// ─── SummaryStats ─────────────────────────────────────────────────────────────

type SummaryStatsProps = {
  totalProductionHours: number
  avgUtilisation: number
  activeKitchens: number
  activeLines: number
  totalQuantity: number
  /** Bottle-format changeover cost this week, summed off the changeover events
   *  the engine emitted. Read from the events rather than re-priced against the
   *  rate table, so a stored schedule keeps reporting what it cost when it ran
   *  even if the table is edited afterwards. */
  formatChangeoverCost: number
}

const SummaryStats = ({
  totalProductionHours,
  avgUtilisation,
  activeKitchens,
  activeLines,
  totalQuantity,
  formatChangeoverCost,
}: SummaryStatsProps) => (
  <Box
    sx={{
      display: 'flex',
      gap: 2,
      flexWrap: 'wrap',
      alignItems: 'center',
      px: 2,
      py: 1,
      bgcolor: '#f5f5f5',
      borderBottom: '1px solid #e0e0e0',
    }}
  >
    <Chip
      icon={<ScheduleIcon sx={{ fontSize: '0.85rem' }} />}
      label={`Production: ${totalProductionHours.toFixed(1)}h`}
      size="small"
      variant="outlined"
      sx={{ fontSize: '0.7rem' }}
    />
    {formatChangeoverCost > 0 && (
      <Chip
        label={`Format changeover cost: ${formatChangeoverCost.toLocaleString()}`}
        size="small"
        variant="outlined"
        sx={{ fontSize: '0.7rem' }}
        title="Total cost of bottle-format changes in this week's schedule."
      />
    )}
    <Chip
      label={`Utilisation: ${avgUtilisation.toFixed(1)}%`}
      size="small"
      variant="outlined"
      color={
        avgUtilisation > 70
          ? 'success'
          : avgUtilisation > 40
            ? 'warning'
            : 'default'
      }
      sx={{ fontSize: '0.7rem' }}
    />
    <Chip
      label={`Kitchens: ${activeKitchens}`}
      size="small"
      variant="outlined"
      sx={{ fontSize: '0.7rem' }}
    />
    <Chip
      label={`Lines: ${activeLines}`}
      size="small"
      variant="outlined"
      sx={{ fontSize: '0.7rem' }}
    />
    <Chip
      label={`Qty: ${totalQuantity.toLocaleString()}`}
      size="small"
      variant="outlined"
      sx={{ fontSize: '0.7rem' }}
    />
  </Box>
)

// ─── Main Component ───────────────────────────────────────────────────────────

export const ScheduleFullGanttView = ({
  schedulerId,
  iteration,
  selectedWeek,
  onWeekChange,
  onBatchSelect,
  notProducedIds,
  onToggleNotProduced,
  consolidatedWeeks,
}: {
  schedulerId: string
  iteration: string
  selectedWeek?: number
  onWeekChange?: (week: number) => void
  onBatchSelect?: (
    resourceName: string,
    resourceType: 'kitchen' | 'line',
    productCode: string,
    startTime: number,
    endTime: number
  ) => void
  notProducedIds?: Set<string>
  onToggleNotProduced?: (barKey: string) => void
  // Sorted list of week numbers consolidated into a single solve. When
  // present and length >= 2, the header shows a multi-week CSV export
  // button that reconstructs the full consolidated horizon by adding
  // `slice_idx * 168` back to each per-week row's time fields.
  consolidatedWeeks?: number[]
}) => {
  const [internalWeek, setInternalWeek] = useState(1)
  const week = selectedWeek ?? internalWeek
  const setWeek = onWeekChange ?? setInternalWeek
  const ganttRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()

  // The answer currently on screen (balanced / fastest / fewest_cleans /
  // least_disruption).
  // `useSchedulerData` below picks this up from the same context on its own;
  // it is needed explicitly here because the export handlers name their
  // files after it, and the multi-week one builds a cache key by hand.
  // Declared above them all so none closes over it before its declaration.
  const scheduleVariant = useContext(ScheduleVariantContext)

  // ── Batch comments ────────────────────────────────────────────────────────
  // One query per week for the whole chart; every bar reads its own from the
  // index. Writing is withheld on an alternative answer for the same reason
  // cell edits are (see ScheduleVariantContext): only the default answer's
  // rows are this iteration's own.
  const { byAnchor: commentIndex } = useCommentIndex(
    schedulerId,
    iteration,
    week
  )
  const commentsReadOnly = scheduleVariant !== 'balanced'
  const currentUserEmail = useCurrentUserEmail()
  const [commentTarget, setCommentTarget] = useState<CommentTarget | null>(null)
  const [commentPosition, setCommentPosition] = useState<{
    top: number
    left: number
  } | null>(null)
  const openComments = useCallback(
    (target: CommentTarget, position: { top: number; left: number }) => {
      setCommentTarget(target)
      setCommentPosition(position)
    },
    []
  )
  const closeComments = useCallback(() => {
    setCommentTarget(null)
    setCommentPosition(null)
  }, [])
  // Read live out of the index so a post or a delete redraws the open thread.
  const openThread = useMemo(
    () =>
      commentsFor(
        commentIndex,
        commentTarget ?? undefined,
        week,
        commentTarget ?? undefined
      ),
    [commentIndex, commentTarget, week]
  )
  /** Spread into every GanttRow — individual props, so memo still compares. */
  const commentProps = {
    commentIndex,
    commentWeek: week,
    onOpenComments: openComments,
    commentsReadOnly,
  }

  const handleExportPNG = useCallback(async () => {
    if (!ganttRef.current) return
    try {
      const html2canvas = (await import('html2canvas')).default
      const canvas = await html2canvas(ganttRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
      })
      canvas.toBlob((blob) => {
        if (!blob) return
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `schedule-week-${week}-${scheduleVariant}.png`
        a.click()
        URL.revokeObjectURL(url)
      })
    } catch {
      // html2canvas not available
    }
  }, [week, scheduleVariant])

  const [scheduleSubTab, setScheduleSubTab] = useState('timeline')

  // Fetch schedule output data
  const { data: scheduleData, isLoading: isScheduleLoading } = useSchedulerData(
    schedulerId,
    iteration,
    week
  )

  const handleExportCSV = useCallback(() => {
    const rows = (scheduleData?.data ?? []) as InputRow[]
    if (!rows.length) return
    const headers = [
      'productCode',
      'kitchen',
      'line',
      'kitchen_start_time',
      'kitchen_end_time',
      'line_start_time',
      'line_end_time',
      'quantity',
      'base_material',
      'runRateBase',
      'runRateLine',
      'fixed',
    ]
    const csv = [
      headers.join(','),
      ...rows.map((r: any) =>
        headers.map((h) => JSON.stringify(r[h] ?? '')).join(',')
      ),
    ].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    // The answer belongs in the name: the rows are right, but once the file
    // is on disk nothing else says whether it is the default answer or an
    // alternative the planner had selected.
    a.download = `schedule-week-${week}-${scheduleVariant}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }, [scheduleData, week, scheduleVariant])

  const handleExportMultiWeekCSV = useCallback(async () => {
    if (!consolidatedWeeks || consolidatedWeeks.length < 2) return
    const sorted = [...consolidatedWeeks].sort((a, b) => a - b)
    const earliest = sorted[0]

    // Per-week clipped output: a spanning run appears once per week it
    // touches, with its time fields clipped to that week's [0, 168] slice.
    // E.g. a kitchen run at absolute hours 140–210 over weeks 1–2 emits
    //   { weekNumber: 1, kitchen_start_time: 140, kitchen_end_time: 168 }
    //   { weekNumber: 2, kitchen_start_time: 0,   kitchen_end_time: 42  }
    // scheduler_api overlap-buckets the same row into both weeks' deltas
    // (with absolute times), so we read each week's cache and clip locally.
    //
    // `request` (axios-client) returns the full AxiosResponse, which is
    // what react-query stores in the cache. `useSchedulerData`'s
    // `select: ({ data }) => data` only applies to hook consumers, not to a
    // direct cache read — so the cached shape is
    // `AxiosResponse<{ data: InputRow[] }>` and the rows live at
    // `cached.data.data`, not `cached.data`. ScheduleControlPanel
    // prefetches all 8 weeks via useSchedulerData so cache hits are the
    // common path.
    const TIME_FIELDS = [
      'kitchen_start_time',
      'kitchen_end_time',
      'line_start_time',
      'line_end_time',
      'kitchen_clean_start_time',
      'kitchen_clean_end_time',
      'kitchen_preclean_start_time',
      'kitchen_preclean_end_time',
      'line_clean_start_time',
      'line_clean_end_time',
      'line_preclean_start_time',
      'line_preclean_end_time',
    ] as const
    const clip = (v: unknown, baseHour: number): number | unknown => {
      if (typeof v !== 'number') return v
      return Math.max(0, Math.min(HOURS, v - baseHour))
    }

    const allRows: Array<Record<string, unknown>> = []
    for (const w of sorted) {
      // useSchedulerData's cache key carries BOTH trailing segments — the
      // mid-week reschedule variant (default 'original') and the reported
      // answer. The answer MUST be the one on screen, not a hardcoded
      // 'balanced': ScheduleControlPanel's prefetch writes under the
      // selected answer, so with Fastest showing, a 'balanced' lookup
      // missed and this exported nothing — or, if Balanced had been viewed
      // earlier in the session, quietly exported the wrong schedule.
      //
      // `fetchQuery` rather than `getQueryData` for the same reason
      // SchedulerCsvDownload uses it: a miss then fetches that week instead
      // of dropping it, so the export can't come back silently short.
      const cached = await queryClient.fetchQuery<{
        data?: { data?: InputRow[] }
      }>(
        ['scheduler', schedulerId, iteration, w, 'original', scheduleVariant],
        () =>
          fetchSchedulerData(
            schedulerId,
            iteration,
            w,
            'original',
            scheduleVariant
          ),
        { staleTime: Infinity }
      )
      const rows = (cached?.data?.data ?? []) as InputRow[]
      const weekBaseHour = (w - earliest) * HOURS
      for (const r of rows) {
        const rec = r as unknown as Record<string, unknown>
        const out: Record<string, unknown> = { ...rec, weekNumber: w }
        for (const f of TIME_FIELDS) {
          if (typeof rec[f] === 'number') {
            out[f] = clip(rec[f], weekBaseHour)
          }
        }
        allRows.push(out)
      }
    }

    if (!allRows.length) {
      // With the fetch above, this now means the weeks genuinely have no
      // rows for this answer — not a cache miss.
      // eslint-disable-next-line no-console
      console.warn('[multi-week CSV] No rows found for selected weeks', sorted)
      return
    }
    const headers = [
      'weekNumber',
      'productCode',
      'kitchen',
      'line',
      'kitchen_start_time',
      'kitchen_end_time',
      'line_start_time',
      'line_end_time',
      'quantity',
      'base_material',
      'runRateBase',
      'runRateLine',
      'fixed',
    ]
    const csv = [
      headers.join(','),
      ...allRows.map((r) =>
        headers.map((h) => JSON.stringify(r[h] ?? '')).join(',')
      ),
    ].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `schedule-multi-week-W${sorted[0]}-W${sorted[sorted.length - 1]}-${scheduleVariant}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }, [consolidatedWeeks, queryClient, schedulerId, iteration, scheduleVariant])

  // Fetch downtime data
  const { data: downtimeData, isLoading: isDowntimeLoading } =
    useScheduleProductionRequirements(
      schedulerId,
      'workstation-downtime',
      iteration,
      week
    )

  // Fetch additional data for sub-tabs
  const { data: syncLinesResponse = { data: [] } } =
    useScheduleProductionRequirements(
      schedulerId,
      'synchronized-lines',
      iteration
    )
  const { data: constraintsResponse = { data: [] } } =
    useScheduleProductionRequirements(
      schedulerId,
      'resource-and-material-constraints',
      iteration
    )
  const { data: productionReqResponse = { data: [] } } =
    useScheduleProductionRequirements(
      schedulerId,
      'production-requirements',
      iteration,
      week
    )

  // Helper: extract array from response (handles { data: [...] } or [...] or undefined)
  const toArray = (val: unknown): unknown[] => {
    if (Array.isArray(val)) return val
    if (
      val &&
      typeof val === 'object' &&
      'data' in val &&
      Array.isArray((val as Record<string, unknown>).data)
    ) {
      return (val as Record<string, unknown>).data as unknown[]
    }
    return []
  }

  const outputRows = toArray(scheduleData?.data) as InputRow[]

  // Expand notProducedIds: if ALL line products from a kitchen batch are not-produced,
  // the kitchen batch is also not-produced. And vice versa: clicking a kitchen bar
  // marks all its line products as not-produced. Shared with ScheduleControlPanel's
  // own expansion (and the mid-week reschedule dialog) — see expandNotProducedKeys
  // in Scheduler.data.ts for the key-format contract with scheduler_api.
  const expandedNotProducedIds = useMemo(() => {
    if (!notProducedIds || notProducedIds.size === 0 || outputRows.length === 0)
      return notProducedIds
    return expandNotProducedKeys(notProducedIds, outputRows)
  }, [notProducedIds, outputRows])

  const syncLines = toArray(
    syncLinesResponse?.data
  ) as SynchronizedLinesRowType[]
  const constraints = toArray(
    constraintsResponse?.data
  ) as SchedulerResourceAndMaterialConstraintsRowType[]
  const productionReqs = toArray(
    productionReqResponse?.data
  ) as ProductionRequirementRowType[]

  // Current hour in the week (Mon 00:00 = 0, Sun 24:00 = 168)
  const currentHourInWeek = useMemo(() => {
    const now = new Date()
    const day = now.getDay() // 0=Sun, 1=Mon
    const mondayOffset = day === 0 ? 6 : day - 1
    return mondayOffset * 24 + now.getHours() + now.getMinutes() / 60
  }, [])

  const isLoading = isScheduleLoading || isDowntimeLoading

  // Build downtime lookup: resource name -> intervals
  const downtimeMap = useMemo(() => {
    const map: Record<string, DowntimeInterval[]> = {}
    if (!downtimeData?.data) return map
    const rows = downtimeData.data as WorkstationDownTimeRowType[]
    for (const row of rows) {
      const intervals = parseDowntimeStr(row.downTime)
      map[row.resource] = intervals.map(([start, end]) => ({ start, end }))
    }
    return map
  }, [downtimeData])

  // In multi-week consolidated mode, the per-week Delta carries ABSOLUTE
  // consolidated-horizon times. Subtract a per-week base hour so the
  // 0-168 Gantt window shows that week's slice — a row at absolute hour
  // 200 (in slice 1) renders at hour 32 when viewing week 2; a spanning
  // row at 140-210 renders 140-168 in week 1 and 0-42 in week 2 (the
  // server writes the same row to both weeks' folders).
  const weekBaseHour = useMemo(() => {
    if (!consolidatedWeeks || consolidatedWeeks.length < 2) return 0
    return (week - consolidatedWeeks[0]) * HOURS
  }, [consolidatedWeeks, week])

  // Process schedule rows into grouped resource data
  const {
    kitchenResources,
    lineResources,
    cipResources,
    byProductLanes,
    summaryStats,
  } = useMemo(() => {
    const kitchenMap = new Map<string, ResourceData>()
    const lineMap = new Map<string, ResourceData>()

    const rawRows = scheduleData?.data
    if (!rawRows || !Array.isArray(rawRows) || rawRows.length === 0) {
      return {
        kitchenResources: [],
        lineResources: [],
        cipResources: [],
        byProductLanes: [],
        summaryStats: {
          totalProductionHours: 0,
          avgUtilisation: 0,
          activeKitchens: 0,
          activeLines: 0,
          totalQuantity: 0,
          formatChangeoverCost: 0,
        },
      }
    }

    // Window time fields by `weekBaseHour`. Numeric fields go through
    // straight subtraction; `additional_*` JSON strings are re-parsed,
    // shifted, and re-serialised so embedded start_time / end_time also
    // map into the current week's 0-168 view. Non-numeric / missing
    // values pass through untouched.
    const TIME_FIELDS = [
      'kitchen_start_time',
      'kitchen_end_time',
      'line_start_time',
      'line_end_time',
      'kitchen_clean_start_time',
      'kitchen_clean_end_time',
      'kitchen_preclean_start_time',
      'kitchen_preclean_end_time',
      'line_clean_start_time',
      'line_clean_end_time',
      'line_preclean_start_time',
      'line_preclean_end_time',
      // Scalar changeovers count too — see ClipToWeek.ts.
      'kitchen_changeover_start_time',
      'kitchen_changeover_end_time',
      'line_changeover_start_time',
      'line_changeover_end_time',
    ] as const
    const ADDITIONAL_CIP_FIELDS = [
      'additional_kitchen_cips',
      'additional_line_cips',
    ] as const

    const windowRow = (r: InputRow): InputRow => {
      if (weekBaseHour === 0) return r
      const out: Record<string, unknown> = { ...r }
      for (const f of TIME_FIELDS) {
        const v = (r as unknown as Record<string, unknown>)[f]
        if (typeof v === 'number') out[f] = v - weekBaseHour
      }
      for (const f of ADDITIONAL_CIP_FIELDS) {
        const v = (r as unknown as Record<string, unknown>)[f]
        if (typeof v === 'string' && v) {
          try {
            // Wire format is postprocess.rs::cip_events_to_json —
            // {"cip":…,"cip_id":…,"start":…,"end":…,"reason":…}. NOT
            // `start_time`/`end_time`: reading those gave undefined, so the
            // shift produced NaN and left the real values absolute.
            const parsed = JSON.parse(v) as Array<{
              start: number
              end: number
              cip?: string
            }>
            // Drop events outside this week's slice rather than clamping
            // them — a clamped one would draw a phantom clean at hour 0.
            const shifted: typeof parsed = []
            for (const p of parsed) {
              const relS = p.start - weekBaseHour
              const relE = p.end - weekBaseHour
              if (relE <= 0 || relS >= HOURS) continue
              shifted.push({
                ...p,
                start: Math.max(0, Math.min(HOURS, relS)),
                end: Math.max(0, Math.min(HOURS, relE)),
              })
            }
            out[f] = JSON.stringify(shifted)
          } catch {
            // Leave malformed JSON alone — the downstream parser is
            // already wrapped in a try/catch and will skip it.
          }
        }
      }
      return out as InputRow
    }

    const rows = (rawRows as InputRow[]).map(windowRow)

    let totalProdHours = 0
    let totalQuantity = 0

    for (const row of rows) {
      // ── Kitchen ──
      if (row.kitchen) {
        if (!kitchenMap.has(row.kitchen)) {
          kitchenMap.set(row.kitchen, {
            name: row.kitchen,
            type: 'kitchen',
            productionBars: [],
            cleaningBars: [],
            downtimeIntervals: downtimeMap[row.kitchen] || [],
          })
        }
        const kResource = kitchenMap.get(row.kitchen)!

        // Production bar
        if (
          row.kitchen_start_time != null &&
          row.kitchen_end_time != null &&
          row.kitchen_end_time > row.kitchen_start_time
        ) {
          const duration = row.kitchen_end_time - row.kitchen_start_time
          totalProdHours += duration
          totalQuantity += row.quantity || 0
          kResource.productionBars.push({
            start: row.kitchen_start_time,
            end: row.kitchen_end_time,
            productCode: row.base_material || row.productCode,
            productDescription:
              row.base_material_description || row.product_description || '',
            baseMaterial: row.base_material || '',
            quantity: row.quantity || 0,
            runRate: row.runRateBase || 0,
            rowId: row.id,
            kitchen: row.kitchen,
            anchor: {
              resourceName: row.kitchen,
              resourceType: 'kitchen',
              productCode: row.base_material || row.productCode,
            },
          })
        }

        // Kitchen cleaning
        if (
          row.kitchen_clean_start_time != null &&
          row.kitchen_clean_end_time != null &&
          row.kitchen_clean_end_time > row.kitchen_clean_start_time
        ) {
          kResource.cleaningBars.push({
            start: row.kitchen_clean_start_time,
            end: row.kitchen_clean_end_time,
            cip: row.kitchenCIP || '',
            type: 'clean',
          })
        }

        // Kitchen preclean
        if (
          row.kitchen_preclean_start_time != null &&
          row.kitchen_preclean_end_time != null &&
          row.kitchen_preclean_end_time > row.kitchen_preclean_start_time
        ) {
          kResource.cleaningBars.push({
            start: row.kitchen_preclean_start_time,
            end: row.kitchen_preclean_end_time,
            cip: row.kitchenPrecleanCIP || '',
            type: 'preclean',
          })
        }

        // Additional kitchen CIPs
        if (row.additional_kitchen_cips) {
          try {
            // Keys are `start`/`end` (postprocess.rs::cip_events_to_json).
            // With `start_time`/`end_time` the guard below compared
            // undefined > undefined, so out-of-band kitchen CIPs never drew.
            const additionalCips = JSON.parse(row.additional_kitchen_cips) as {
              start: number
              end: number
              cip: string
            }[]
            for (const cip of additionalCips) {
              if (cip.end > cip.start) {
                kResource.cleaningBars.push({
                  start: cip.start,
                  end: cip.end,
                  cip: cip.cip || '',
                  type: 'clean',
                })
              }
            }
          } catch {
            // ignore malformed JSON
          }
        }
      }

      // ── Holding tank: the drain that occupies neither slot ──
      //
      // On a fill (`KTN1 -> TANK01`) and on a drain to a line
      // (`TANK01 -> LINE1`) the vessel IS one of the two assets, so the kitchen
      // and line blocks around this one already draw it. A drain into a second
      // kitchen (`TANK02 -> PKN12 -> CTL01`) is the shape that does not: both
      // slots hold real assets and the vessel supplies the run's input as a
      // maturity floor and a supply cap rather than as an endpoint. Without
      // this block that tank's lane shows its fill and then nothing for the
      // rest of the horizon, and the Kitchen2 run appears to come from nowhere.
      //
      // The bar spans the KITCHEN window because that is when Kitchen2 is
      // drawing and the vessel is emptying — not the line window, which is the
      // same run theta hours later and already drawn on the real line.
      //
      // Deliberately NOT added to `totalProdHours` / `totalQuantity`: this is
      // the same physical run as the Kitchen2 bar, shown a second time from the
      // vessel's point of view, and counting it would double the plant totals.
      const tankName = row.tankId
      if (tankName && tankName !== row.kitchen && tankName !== row.line) {
        if (!lineMap.has(tankName)) {
          lineMap.set(tankName, {
            name: tankName,
            type: 'line',
            productionBars: [],
            cleaningBars: [],
            downtimeIntervals: downtimeMap[tankName] || [],
          })
        }
        const tResource = lineMap.get(tankName)!
        if (
          row.kitchen_start_time != null &&
          row.kitchen_end_time != null &&
          row.kitchen_end_time > row.kitchen_start_time
        ) {
          tResource.productionBars.push({
            start: row.kitchen_start_time,
            end: row.kitchen_end_time,
            productCode: row.productCode || '',
            productDescription: row.product_description || '',
            baseMaterial: row.base_material || '',
            quantity: row.quantity || 0,
            runRate: row.runRateBase || 0,
            rowId: row.id,
            kitchen: row.kitchen,
            anchor: {
              resourceName: tankName,
              resourceType: 'line',
              productCode: row.productCode || '',
            },
          })
        }
      }

      // ── Line ──
      if (row.line) {
        if (!lineMap.has(row.line)) {
          lineMap.set(row.line, {
            name: row.line,
            type: 'line',
            productionBars: [],
            cleaningBars: [],
            downtimeIntervals: downtimeMap[row.line] || [],
          })
        }
        const lResource = lineMap.get(row.line)!

        // Production bar
        if (
          row.line_start_time != null &&
          row.line_end_time != null &&
          row.line_end_time > row.line_start_time
        ) {
          lResource.productionBars.push({
            start: row.line_start_time,
            end: row.line_end_time,
            productCode: row.productCode || '',
            productDescription: row.product_description || '',
            baseMaterial: row.base_material || '',
            quantity: row.quantity || 0,
            runRate: row.runRateLine || 0,
            rowId: row.id,
            kitchen: row.kitchen,
            anchor: {
              resourceName: row.line,
              resourceType: 'line',
              productCode: row.productCode || '',
            },
          })
        }

        // Line cleaning
        if (
          row.line_clean_start_time != null &&
          row.line_clean_end_time != null &&
          row.line_clean_end_time > row.line_clean_start_time
        ) {
          lResource.cleaningBars.push({
            start: row.line_clean_start_time,
            end: row.line_clean_end_time,
            cip: row.lineCIP || '',
            type: 'clean',
          })
        }

        // Line preclean
        if (
          row.line_preclean_start_time != null &&
          row.line_preclean_end_time != null &&
          row.line_preclean_end_time > row.line_preclean_start_time
        ) {
          lResource.cleaningBars.push({
            start: row.line_preclean_start_time,
            end: row.line_preclean_end_time,
            cip: row.linePrecleanCIP || '',
            type: 'preclean',
          })
        }

        // Additional line CIPs
        if (row.additional_line_cips) {
          try {
            // Same key fix as the kitchen CIPs above.
            const additionalCips = JSON.parse(row.additional_line_cips) as {
              start: number
              end: number
              cip: string
            }[]
            for (const cip of additionalCips) {
              if (cip.end > cip.start) {
                lResource.cleaningBars.push({
                  start: cip.start,
                  end: cip.end,
                  cip: cip.cip || '',
                  type: 'clean',
                })
              }
            }
          } catch {
            // ignore malformed JSON
          }
        }
      }
    }

    // Aggregate CIP cleaning events by CIP name
    const cipMap = new Map<string, ResourceData>()
    const allCleaningBars = [
      ...Array.from(kitchenMap.values()).flatMap((r) =>
        r.cleaningBars.map((b) => ({ ...b, asset: r.name }))
      ),
      ...Array.from(lineMap.values()).flatMap((r) =>
        r.cleaningBars.map((b) => ({ ...b, asset: r.name }))
      ),
    ]
    for (const bar of allCleaningBars) {
      const cipName = bar.cip
      if (!cipName) continue
      if (!cipMap.has(cipName)) {
        cipMap.set(cipName, {
          name: cipName,
          type: 'cip',
          productionBars: [],
          cleaningBars: [],
          downtimeIntervals: downtimeMap[cipName] || [],
        })
      }
      // Show CIP activity as cleaning bars (orange) on the CIP row
      cipMap.get(cipName)!.cleaningBars.push({
        start: bar.start,
        end: bar.end,
        cip: (bar as { asset?: string }).asset || '',
        type: bar.type,
      })
    }

    // Sort alphabetically
    const kitchenResources = Array.from(kitchenMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    )
    const lineResources = Array.from(lineMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    )
    const cipResources = Array.from(cipMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    )

    // Calculate utilisation
    const allResources = [...kitchenResources, ...lineResources]
    const totalResourceHours = allResources.length * HOURS
    const totalBusyHours = allResources.reduce((sum, r) => {
      const busy = r.productionBars.reduce(
        (s, bar) => s + (bar.end - bar.start),
        0
      )
      return sum + busy
    }, 0)
    const avgUtilisation =
      totalResourceHours > 0 ? (totalBusyHours / totalResourceHours) * 100 : 0

    // ── By-product lanes: transpose the schedule so each base material is a
    // header lane (its kitchen runs) with its products nested beneath (their
    // line runs). Bars are coloured by feeding kitchen (getKitchenColor), same
    // as every other view; the equipment producing each run is the bar label. ──
    const bmGroups = new Map<string, InputRow[]>()
    for (const row of rows) {
      if (!row.base_material) continue
      const hasKitchen =
        !!row.kitchen && row.kitchen_end_time > row.kitchen_start_time
      const hasLine = !!row.line && row.line_end_time > row.line_start_time
      if (!hasKitchen && !hasLine) continue
      const arr = bmGroups.get(row.base_material) || []
      arr.push(row)
      bmGroups.set(row.base_material, arr)
    }
    const byProductLanes: ProductScheduleLane[] = Array.from(bmGroups.entries())
      .map(([bm, bmRows]) => {
        const description =
          bmRows.find((r) => r.base_material_description)
            ?.base_material_description || ''
        // Kitchen bars — one per distinct block, labelled by the kitchen.
        const seenK = new Set<string>()
        const kitchenBars: ProductionBar[] = []
        for (const r of bmRows) {
          if (!r.kitchen || !(r.kitchen_end_time > r.kitchen_start_time))
            continue
          const key = `${r.kitchen}_${r.kitchen_start_time}_${r.kitchen_end_time}`
          if (seenK.has(key)) continue
          seenK.add(key)
          kitchenBars.push({
            start: r.kitchen_start_time,
            end: r.kitchen_end_time,
            productCode: r.kitchen,
            productDescription: description,
            baseMaterial: bm,
            quantity: r.quantity || 0,
            runRate: r.runRateBase || 0,
            kitchen: r.kitchen,
            // This view swaps the pair round — the row is the base material
            // and the bar is labelled with the kitchen — so the anchor is
            // spelled out rather than read off the bar, keeping it identical
            // to the one the Timeline tab writes for the same batch.
            anchor: {
              resourceName: r.kitchen,
              resourceType: 'kitchen',
              productCode: bm,
            },
          })
        }
        const kitchen: ResourceData = {
          name: bm,
          type: 'kitchen',
          productionBars: kitchenBars,
          cleaningBars: [],
          downtimeIntervals: [],
          description,
        }
        // Products nested under this base material — line bars by product,
        // labelled by the line.
        const byProd = new Map<string, InputRow[]>()
        for (const r of bmRows) {
          if (!r.productCode) continue
          const arr = byProd.get(r.productCode) || []
          arr.push(r)
          byProd.set(r.productCode, arr)
        }
        const products: ResourceData[] = Array.from(byProd.entries())
          .map(([pCode, pRows]) => {
            const pDesc =
              pRows.find((r) => r.product_description)?.product_description ||
              ''
            const seenL = new Set<string>()
            const lineBars: ProductionBar[] = []
            for (const r of pRows) {
              if (!r.line || !(r.line_end_time > r.line_start_time)) continue
              const key = `${r.line}_${r.line_start_time}_${r.line_end_time}`
              if (seenL.has(key)) continue
              seenL.add(key)
              lineBars.push({
                start: r.line_start_time,
                end: r.line_end_time,
                productCode: r.line,
                productDescription: pDesc,
                baseMaterial: bm,
                quantity: r.quantity || 0,
                runRate: r.runRateLine || 0,
                kitchen: r.kitchen,
                anchor: {
                  resourceName: r.line,
                  resourceType: 'line',
                  productCode: pCode,
                },
              })
            }
            return {
              name: pCode,
              type: 'line' as ResourceType,
              productionBars: lineBars,
              cleaningBars: [],
              downtimeIntervals: [],
              description: pDesc,
            }
          })
          .sort((a, b) => a.name.localeCompare(b.name))
        return { baseMaterial: bm, description, kitchen, products }
      })
      .sort((a, b) => a.baseMaterial.localeCompare(b.baseMaterial))

    // Bottle-format changeover cost: sum the `cost` the engine stamped on each
    // format-change event. Deduplicated on (line, start) because the schedule
    // table repeats a row per product sharing a batch, so the same event
    // arrives several times.
    let formatChangeoverCost = 0
    const seenFormatEvents = new Set<string>()
    for (const row of rows) {
      for (const evt of parseLineChangeoverEvents(
        row.additional_line_changeovers
      )) {
        if (!evt.reason?.startsWith('format_change')) continue
        if (typeof evt.cost !== 'number') continue
        const key = `${row.line}_${evt.start}_${evt.end}`
        if (seenFormatEvents.has(key)) continue
        seenFormatEvents.add(key)
        formatChangeoverCost += evt.cost
      }
    }

    return {
      kitchenResources,
      lineResources,
      cipResources,
      byProductLanes,
      summaryStats: {
        totalProductionHours: totalProdHours,
        avgUtilisation,
        activeKitchens: kitchenResources.filter(
          (k) => k.productionBars.length > 0
        ).length,
        activeLines: lineResources.filter((l) => l.productionBars.length > 0)
          .length,
        totalQuantity,
        formatChangeoverCost,
      },
    }
  }, [scheduleData, downtimeMap, weekBaseHour])

  // ── Loading or empty state (keep week selector visible) ──
  if (
    isLoading ||
    (kitchenResources.length === 0 && lineResources.length === 0)
  ) {
    return (
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScheduleIcon sx={{ fontSize: '1rem', color: '#666' }} />
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
              Factory Schedule
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '0.75rem', color: '#666' }}>
              Week:
            </Typography>
            <Select
              value={week}
              onChange={(e) => setWeek(Number(e.target.value))}
              size="small"
              sx={{ fontSize: '0.75rem', height: 28, minWidth: 60 }}
            >
              {Array.from({ length: 8 }, (_, i) => i + 1).map((w) => (
                <MenuItem key={w} value={w} sx={{ fontSize: '0.75rem' }}>
                  {w}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 250,
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {isLoading ? (
            <>
              <CircularProgress size={28} />
              <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                Loading Week {week}...
              </Typography>
            </>
          ) : (
            <>
              <ScheduleIcon sx={{ fontSize: 40, color: '#bdbdbd' }} />
              <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary' }}>
                No schedule output for Week {week}.
              </Typography>
            </>
          )}
        </Box>
      </Paper>
    )
  }

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      {/* Controls bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScheduleIcon sx={{ fontSize: '1rem', color: '#666' }} />
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
            Factory Schedule
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Export as PNG">
            <IconButton
              size="small"
              onClick={handleExportPNG}
              sx={{ color: '#666' }}
            >
              <ImageIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export current week as CSV">
            <IconButton
              size="small"
              onClick={handleExportCSV}
              sx={{ color: '#666' }}
            >
              <DownloadIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          {consolidatedWeeks && consolidatedWeeks.length >= 2 && (
            <Tooltip
              title={`Export full multi-week schedule (W${consolidatedWeeks[0]}–W${consolidatedWeeks[consolidatedWeeks.length - 1]}) as CSV`}
            >
              <IconButton
                size="small"
                onClick={handleExportMultiWeekCSV}
                sx={{ color: '#1976d2' }}
              >
                <DownloadIcon sx={{ fontSize: 18 }} />
                <Typography
                  sx={{ fontSize: '0.65rem', ml: 0.25, fontWeight: 600 }}
                >
                  {consolidatedWeeks.length}w
                </Typography>
              </IconButton>
            </Tooltip>
          )}
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <Typography sx={{ fontSize: '0.75rem', color: '#666' }}>
            Week:
          </Typography>
          <Select
            value={week}
            onChange={(e) => setWeek(Number(e.target.value))}
            size="small"
            sx={{ fontSize: '0.75rem', height: 28, minWidth: 60 }}
          >
            {Array.from({ length: 8 }, (_, i) => i + 1).map((w) => (
              <MenuItem key={w} value={w} sx={{ fontSize: '0.75rem' }}>
                {w}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      {/* Sub-tabs */}
      <Tabs
        value={scheduleSubTab}
        onChange={(_, v) => setScheduleSubTab(v)}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          minHeight: 36,
          px: 2,
          '& .MuiTab-root': {
            minHeight: 36,
            py: 0.5,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.8rem',
          },
        }}
      >
        <Tab label="Timeline" value="timeline" />
        <Tab label="By Product" value="byProduct" />
        {!isSubTabHidden('crew') && (
          <Tab label="Crew & Capacity" value="crew" />
        )}
        <Tab label="Resource Usage" value="resources" />
        <Tab label="Demand Fulfilment" value="demand" />
        {!isSubTabHidden('temperature') && (
          <Tab label="Sync Groups" value="temperature" />
        )}
      </Tabs>

      {scheduleSubTab === 'crew' && !isSubTabHidden('crew') && (
        <ScheduleCrewUsageView
          outputRows={outputRows}
          synchronizedLines={syncLines}
        />
      )}

      {scheduleSubTab === 'resources' && (
        <ScheduleResourceUsageView
          scheduleId={schedulerId}
          iteration={iteration}
          selectedWeek={week}
          outputRows={outputRows}
          kitchenResources={kitchenResources}
          cipResources={cipResources}
          constraints={constraints}
        />
      )}

      {scheduleSubTab === 'demand' && (
        <ScheduleDemandFulfilmentView
          outputRows={outputRows}
          productionRequirements={productionReqs}
        />
      )}

      {scheduleSubTab === 'temperature' && !isSubTabHidden('temperature') && (
        <ScheduleTemperatureView
          outputRows={outputRows}
          synchronizedLines={syncLines}
          productionRequirements={productionReqs}
        />
      )}

      {scheduleSubTab === 'byProduct' && (
        <>
          <SummaryStats {...summaryStats} />
          <Box sx={{ px: 2, py: 0.5, borderBottom: '1px solid #e0e0e0' }}>
            <Typography sx={{ fontSize: '0.6rem', color: '#999' }}>
              Lanes are base materials (kitchen runs) with their products nested
              beneath (line runs). Bars are coloured by base material; the label
              shows the equipment producing each run.
            </Typography>
          </Box>
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: 900, position: 'relative' }}>
              {currentHourInWeek > 0 && currentHourInWeek < HOURS && (
                <Box
                  sx={{
                    position: 'absolute',
                    left:
                      LEFT_COL_WIDTH +
                      (currentHourInWeek / HOURS) * (900 - LEFT_COL_WIDTH),
                    top: 0,
                    bottom: 0,
                    width: 0,
                    borderLeft: '2px dashed #d32f2f',
                    zIndex: 10,
                    pointerEvents: 'none',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 4,
                      left: -14,
                      bgcolor: '#d32f2f',
                      color: 'white',
                      fontSize: '0.5rem',
                      fontWeight: 700,
                      px: 0.5,
                      py: 0.25,
                      borderRadius: '2px',
                      lineHeight: 1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    NOW
                  </Box>
                </Box>
              )}
              <TimeAxis />
              {byProductLanes.length === 0 ? (
                <Box sx={{ p: 2 }}>
                  <Typography
                    color="text.secondary"
                    sx={{ fontSize: '0.8rem' }}
                  >
                    No scheduled production for this week.
                  </Typography>
                </Box>
              ) : (
                byProductLanes.map((lane, idx) => (
                  <Box key={`bm-${lane.baseMaterial}`}>
                    <GanttRow
                      resource={lane.kitchen}
                      isEven={idx % 2 === 0}
                      isHeader
                      icon={
                        <KitchenIcon sx={{ fontSize: 16, color: '#1976d2' }} />
                      }
                      {...commentProps}
                    />
                    {lane.products.map((product, pIdx) => (
                      <GanttRow
                        key={`p-${lane.baseMaterial}-${product.name}`}
                        resource={product}
                        isEven={pIdx % 2 === 0}
                        indent
                        icon={
                          <LineIcon sx={{ fontSize: 15, color: '#2e7d32' }} />
                        }
                        {...commentProps}
                      />
                    ))}
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </>
      )}

      {scheduleSubTab === 'timeline' && (
        <>
          {/* Summary stats */}
          <SummaryStats {...summaryStats} />

          {/* Legend */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              px: 2,
              py: 0.5,
              borderBottom: '1px solid #e0e0e0',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontSize: '0.6rem', color: '#999', mr: 1 }}>
              Legend:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 8,
                  bgcolor: '#4caf50',
                  borderRadius: '1px',
                }}
              />
              <Typography sx={{ fontSize: '0.6rem', color: '#666' }}>
                Production
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 8,
                  bgcolor: '#ff9800',
                  borderRadius: '1px',
                }}
              />
              <Typography sx={{ fontSize: '0.6rem', color: '#666' }}>
                CIP Clean
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 8,
                  background:
                    'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(211,47,47,0.3) 2px, rgba(211,47,47,0.3) 4px)',
                  border: '1px solid rgba(211,47,47,0.4)',
                  borderRadius: '1px',
                }}
              />
              <Typography sx={{ fontSize: '0.6rem', color: '#666' }}>
                Downtime
              </Typography>
            </Box>
          </Box>

          {/* Gantt chart */}
          <Box ref={ganttRef} sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: 900, position: 'relative' }}>
              {/* "Now" indicator line — positioned in the timeline area (after LEFT_COL_WIDTH) */}
              {currentHourInWeek > 0 && currentHourInWeek < HOURS && (
                <Box
                  sx={{
                    position: 'absolute',
                    left:
                      LEFT_COL_WIDTH +
                      (currentHourInWeek / HOURS) * (900 - LEFT_COL_WIDTH),
                    top: 0,
                    bottom: 0,
                    width: 0,
                    borderLeft: '2px dashed #d32f2f',
                    zIndex: 10,
                    pointerEvents: 'none',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 4,
                      left: -14,
                      bgcolor: '#d32f2f',
                      color: 'white',
                      fontSize: '0.5rem',
                      fontWeight: 700,
                      px: 0.5,
                      py: 0.25,
                      borderRadius: '2px',
                      lineHeight: 1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    NOW
                  </Box>
                </Box>
              )}
              {/* Time axis */}
              <TimeAxis />

              {/* Kitchens section */}
              <SectionHeader label="Kitchens" />
              {kitchenResources.map((resource, idx) => (
                <GanttRow
                  key={resource.name}
                  resource={resource}
                  isEven={idx % 2 === 0}
                  onBarClick={(bar, type) => {
                    if (onToggleNotProduced) {
                      onToggleNotProduced(
                        `${bar.productCode}_${resource.name}_${bar.start}`
                      )
                    } else {
                      onBatchSelect?.(
                        resource.name,
                        type === 'cip' ? 'kitchen' : type,
                        bar.productCode,
                        bar.start,
                        bar.end
                      )
                    }
                  }}
                  currentHour={currentHourInWeek}
                  notProducedIds={expandedNotProducedIds}
                  {...commentProps}
                />
              ))}

              {/* Divider */}
              <Divider sx={{ borderColor: '#bdbdbd' }} />

              {/* Lines section */}
              <SectionHeader label="Lines" />
              {lineResources.map((resource, idx) => (
                <GanttRow
                  key={resource.name}
                  resource={resource}
                  isEven={idx % 2 === 0}
                  onBarClick={(bar, type) => {
                    if (onToggleNotProduced) {
                      onToggleNotProduced(
                        `${bar.productCode}_${resource.name}_${bar.start}`
                      )
                    } else {
                      onBatchSelect?.(
                        resource.name,
                        type === 'cip' ? 'line' : type,
                        bar.productCode,
                        bar.start,
                        bar.end
                      )
                    }
                  }}
                  currentHour={currentHourInWeek}
                  notProducedIds={expandedNotProducedIds}
                  {...commentProps}
                />
              ))}

              {/* CIP section */}
              {cipResources.length > 0 && (
                <>
                  <SectionHeader label="CIP Equipment" />
                  {cipResources.map((resource, idx) => (
                    <GanttRow
                      key={resource.name}
                      resource={resource}
                      isEven={idx % 2 === 0}
                    />
                  ))}
                </>
              )}
            </Box>
          </Box>
        </>
      )}

      {/* Batch comments — opened from a bar's hover pop-up in either the
          Timeline or the By Product tab, so it sits outside both. */}
      <ScheduleCommentPopover
        schedulerId={schedulerId}
        iteration={iteration}
        weekNumber={week}
        target={commentTarget}
        position={commentPosition}
        comments={openThread}
        readOnly={commentsReadOnly}
        readOnlyReason={`Viewing the ${scheduleVariant.replace('_', ' ')} answer — comments can only be added on the schedule's own answer.`}
        currentUserEmail={currentUserEmail}
        onClose={closeComments}
      />
    </Paper>
  )
}
